/**
 * Thin wrapper around localStorage for the access token, plus axios
 * interceptors that inject the Bearer header and force a logout when
 * the backend returns 401.
 */

import { api, API } from "./api";

const TOKEN_KEY = "cei.session.token";
const LABEL_KEY = "cei.session.label";
const IS_ADMIN_KEY = "cei.session.is_admin";

export const ADMIN_TOKEN_KEY = "cei.admin.token";

export function setSession({ token, label, is_admin }) {
  localStorage.setItem(TOKEN_KEY, token);
  if (label) localStorage.setItem(LABEL_KEY, label);
  if (typeof is_admin !== "undefined")
    localStorage.setItem(IS_ADMIN_KEY, is_admin ? "1" : "0");
  if (is_admin) localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getLabel() {
  return localStorage.getItem(LABEL_KEY);
}

export function isAdmin() {
  return localStorage.getItem(IS_ADMIN_KEY) === "1";
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LABEL_KEY);
  localStorage.removeItem(IS_ADMIN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

// Attach interceptors to the shared axios instance.
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(error);
  }
);

export async function login(password) {
  // Uses raw fetch because interceptors would inject a stale token.
  const resp = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const msg = body.detail || "Invalid password";
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  const data = await resp.json();
  setSession(data);
  return data;
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch (err) {
    // Logout is best-effort; continue to clear local state regardless.
    console.warn("Server-side logout failed, clearing local session anyway", err);
  }
  clearSession();
}

export async function heartbeat() {
  try {
    await api.post("/auth/heartbeat");
  } catch (err) {
    // Heartbeat errors are non-fatal — a real 401 will already have been
    // handled by the response interceptor. Log and continue.
    if (err?.response?.status && err.response.status !== 401) {
      console.warn("Auth heartbeat failed", err.response.status);
    }
  }
}

// --- Admin-specific helpers: uses its own Bearer token ---
export async function adminLogin(password) {
  const resp = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.detail || "Invalid password");
  }
  const data = await resp.json();
  if (!data.is_admin) {
    throw new Error("This password does not have admin access");
  }
  localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
  return data;
}

export async function adminFetch(path) {
  const token = getAdminToken();
  if (!token) throw new Error("Not authenticated as admin");
  const resp = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    if (resp.status === 401 || resp.status === 403) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.json();
}
